import React, { useRef, useEffect, useState } from "react";
import { ChatMessage } from "../../types/Chatbot/Chat";
import { Box, Typography, Button, Avatar, useTheme, IconButton, Collapse } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import paceAiLogo from "../../assets/Images/ChatAvatar.svg";
import CircularProgress from "@mui/material/CircularProgress";
import { downloadAsCsv, copyToClipboard } from "../../utils/chatUtils";

import styles from "./styles";

interface ChatResponseProps {
  messages: ChatMessage[];
  isLoading: boolean;
  miniChat: boolean;
}

// Handles displaying the chat messages with expandable sections
const ChatResponse: React.FC<ChatResponseProps> = ({ messages, isLoading, miniChat }) => {
  const theme = useTheme();
  const newQuestionRef = useRef<HTMLDivElement | null>(null);

  // Scroll to ensure the new question is at the top and response is visible
  useEffect(() => {
    if (newQuestionRef.current) {
      const container = newQuestionRef.current.parentElement;

      if (container) {
        const offsetTop = newQuestionRef.current.offsetTop - container.offsetTop;
        // Adjust to show the response below
        const scrollPosition = offsetTop - container.clientHeight / 25;
        container.scrollTo({
          top: scrollPosition,
          behavior: "smooth",
        });
      }
    }
  }, [messages]);

  // Expand states for sections
  const [expandedExplanation, setExpandedExplanation] = useState<number | null>(null);
  const [expandedSources, setExpandedSources] = useState<number | null>(null);

  // Toggle explanation section
  const toggleExplanation = (index: number) => {
    setExpandedExplanation((prev) => (prev === index ? null : index));
  };

  // Toggle sources section
  const toggleSources = (index: number) => {
    setExpandedSources((prev) => (prev === index ? null : index));
  };

  return (
    <>
      {messages.length > 0 ? (
        messages.map((message, index) => (
          <Box key={index} sx={styles.containerSpacing} ref={index === messages.length - 1 ? newQuestionRef : null}>
            {/* User Message */}
            <Box sx={styles.messageBox}>
              <Box sx={styles.avatarIcon}>
                <Avatar sx={styles.messageAvatar}></Avatar>
                <Typography variant="body1" sx={styles.messageUser}>
                  You
                </Typography>
              </Box>
              <Typography variant="body1" sx={styles.messageText}>
                {message.question}
              </Typography>
            </Box>

            {/* AI Response */}
            {message.summary && (
              <Box sx={styles.answerBox}>
                <Box sx={styles.avatarIcon}>
                  <img src={paceAiLogo} alt="icon" width="40" height="40" />
                  <Typography variant="body1" sx={styles.messageUser}>
                    PACE AI
                  </Typography>
                </Box>

                <Typography variant="h4" sx={styles.messageHeader}>
                  SUMMARY
                </Typography>
                <Typography variant="body1" sx={{ ...styles.messageText, ...styles.messageAnswer }}>
                  {message.summary}
                </Typography>

                {/* Expandable Explanation Section */}
                <Box>
                  <Box sx={styles.expandableSection} onClick={() => toggleExplanation(index)}>
                    <Typography variant="h4">EXPLANATION</Typography>
                    <IconButton size="small" sx={{ ml: 1 }}>
                      {expandedExplanation === index ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                  <Collapse in={expandedExplanation === index}>
                    <Typography variant="body1" sx={{ ...styles.messageText, ...styles.messageAnswer }}>
                      {message.explanation}
                    </Typography>
                  </Collapse>
                </Box>

                {/* Expandable Sources Section */}
                <Box>
                  <Box sx={styles.expandableSection} onClick={() => toggleSources(index)}>
                    <Typography variant="h4">SOURCES</Typography>
                    <IconButton size="small" sx={{ ml: 1 }}>
                      {expandedSources === index ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                  <Collapse in={expandedSources === index}>
                    {message.sources.map((source, idx) => (
                      <Typography key={idx} variant="body1" sx={{ ...styles.messageText, ...styles.messageSources(theme) }}>
                        {/* <a href={source} target="_blank" rel="noreferrer"> */}
                        {source}
                        {/* </a> */}
                      </Typography>
                    ))}
                  </Collapse>
                </Box>

                {/* Action Buttons */}
                <Box sx={styles.actionContainer}>
                  <Button variant="outlined" color="primary" size="small" sx={styles.actionButton} onClick={() => copyToClipboard(message)}>
                    Copy
                  </Button>
                  <Button variant="outlined" color="primary" size="small" sx={styles.actionButton} onClick={() => downloadAsCsv(message)}>
                    Download
                  </Button>
                </Box>
              </Box>
            )}

            {/* Loading Indicator */}
            {message.summary === "" && index === messages.length - 1 && isLoading && (
              <Box sx={styles.answerBox}>
                <Box sx={styles.avatarIcon}>
                  <img src={paceAiLogo} alt="icon" width="40" height="40" />
                  <Typography variant="body1" sx={styles.messageUser}>
                    PACE AI
                  </Typography>
                </Box>
                <Box sx={styles.loaderContainer}>
                  <CircularProgress size={30} />
                </Box>
              </Box>
            )}
          </Box>
        ))
      ) : (
        <Box sx={styles.emptyState}>
          <Typography color="black" align="center" mt={2} variant={miniChat ? "body1" : "h2"}>
            What can I help with?
          </Typography>
        </Box>
      )}
    </>
  );
};

export default ChatResponse;
